import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Linkedin, Globe, MessageSquare, FileText, AlertCircle, Users, Calendar, TrendingUp, UserPlus, CheckCircle2, CheckCircle, Play, Pause, Lock, Snowflake, Flame, Sun, Clock, Send, Inbox, CalendarClock, XOctagon, MessageCircle, Mail, UserCheck } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { getCampaignLeadStatusTag } from '../../utils/leadStatusBadge';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { useLinkedInCampaignLeads } from '../../hooks/useSupabaseLeads';
import { useAgentSenderName } from '../../hooks/useAgentSenderName';
import { supabase } from '../../lib/supabase';
import StatsCard from '../Dashboard/StatsCard';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import LoadingScreen from '../shared/LoadingScreen';
import LinkedInThreadModal from '../shared/LinkedInThreadModal';
import ConvoSummaryModal from '../shared/ConvoSummaryModal';

const LinkedInCampaignPage: React.FC = () => {
  const tz = useTimezone();
  const navigate = useNavigate();
  const { data: allLinkedInLeads, isLoading, error } = useLinkedInCampaignLeads();
  const { getAgentLabel } = useAgentSenderName('linkedin');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showThreadModal, setShowThreadModal] = useState(false);
  const [selectedThreadLeadId, setSelectedThreadLeadId] = useState('');
  const [selectedThreadLeadName, setSelectedThreadLeadName] = useState('');
  const [selectedThreadCompanyName, setSelectedThreadCompanyName] = useState('');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState('');
  const [selectedSummaryLeadName, setSelectedSummaryLeadName] = useState('');
  const [selectedSummaryCompanyName, setSelectedSummaryCompanyName] = useState('');
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [selectAllLeads, setSelectAllLeads] = useState(false);
  const [activeBulkAction, setActiveBulkAction] = useState<string | null>(null);
  const itemsPerPage = 10;

  const filteredLeads = useMemo(() => {
    return allLinkedInLeads.filter(lead => {
      const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (lead.lead_id || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || lead.lead_status === statusFilter;
      const matchesTemperature = temperatureFilter === 'all' ||
                                (lead.lead_temprature || '').trim().toLowerCase().includes(temperatureFilter.toLowerCase());
      return matchesSearch && matchesStatus && matchesTemperature;
    });
  }, [allLinkedInLeads, searchTerm, statusFilter, temperatureFilter]);

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const linkedInLeads = useMemo(() => {
    return filteredLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLeads, startIndex, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds([]);
    setSelectAllLeads(false);
  }, [searchTerm, statusFilter, temperatureFilter]);

  const handleSelectAll = () => {
    const allFilteredIds = linkedInLeads.map(lead => lead.lead_id).filter(Boolean);
    if (selectedLeadIds.length === allFilteredIds.length && allFilteredIds.length > 0) {
      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } else {
      setSelectedLeadIds(allFilteredIds);
      setSelectAllLeads(false);
    }
  };

  const handleSelectAllLeads = () => {
    const allFilteredIds = filteredLeads.map(lead => lead.lead_id).filter(Boolean);
    setSelectedLeadIds(allFilteredIds);
    setSelectAllLeads(true);
  };

  const handleSelectLead = (lead_id: string) => {
    if (!lead_id) return;

    setSelectedLeadIds(prev => {
      if (prev.includes(lead_id)) {
        setSelectAllLeads(false);
        return prev.filter(leadId => leadId !== lead_id);
      } else {
        return [...prev, lead_id];
      }
    });
  };

  const allCurrentPageSelected = useMemo(() => {
    const allPageIds = linkedInLeads.map(lead => lead.lead_id).filter(Boolean);
    return allPageIds.length > 0 && allPageIds.every(id => selectedLeadIds.includes(id));
  }, [linkedInLeads, selectedLeadIds]);

  const showSelectAllBanner = allCurrentPageSelected && !selectAllLeads && filteredLeads.length > linkedInLeads.length;

  const handleBulkStatusChange = async (status: string) => {
    if (selectedLeadIds.length === 0) return;

    setActiveBulkAction(status);

    try {
      const leadsToProcess = selectAllLeads
        ? filteredLeads.map(lead => lead.lead_id).filter(Boolean)
        : selectedLeadIds;

      if (status === 'on hold') {
        await supabase.from('leads_tracking')
          .update({ la_lead_status: 'on hold', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        await supabase.from('leads_campaigns')
          .update({ status: 'on hold', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'linkedin');
      } else if (status === 'active') {
        const { data: lcRows } = await supabase.from('leads_campaigns')
          .select('lead_id, campaign_step')
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'linkedin');
        const step0Ids = (lcRows || []).filter(r => (r.campaign_step || 0) === 0).map(r => r.lead_id);
        const stepGte1Ids = (lcRows || []).filter(r => (r.campaign_step || 0) >= 1).map(r => r.lead_id);
        if (step0Ids.length > 0) {
          await supabase.from('leads_campaigns')
            .update({ status: 'assigned', updated_at: new Date().toISOString() })
            .in('lead_id', step0Ids)
            .eq('campaign_type', 'linkedin');
          await supabase.from('leads_tracking')
            .update({ la_lead_status: 'assigned', updated_at: new Date().toISOString() })
            .in('lead_id', step0Ids);
        }
        if (stepGte1Ids.length > 0) {
          await supabase.from('leads_campaigns')
            .update({ status: 'follow up', updated_at: new Date().toISOString() })
            .in('lead_id', stepGte1Ids)
            .eq('campaign_type', 'linkedin');
          await supabase.from('leads_tracking')
            .update({ la_lead_status: 'follow up', updated_at: new Date().toISOString() })
            .in('lead_id', stepGte1Ids);
        }
      } else if (status === 'closed') {
        await supabase.from('leads_tracking')
          .update({ la_lead_status: 'closed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        await supabase.from('leads_campaigns')
          .update({ status: 'completed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'linkedin');
      }

      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } catch (error) {
      console.error('Failed to update lead status:', error);
    } finally {
      setActiveBulkAction(null);
    }
  };

  const stats = useMemo(() => {
    const totalConnections = allLinkedInLeads.filter(lead => lead.cr_sent === true || lead.cr_sent === 'true' || lead.cr_sent === '1').length;
    const connectionsAccepted = allLinkedInLeads.filter(lead => {
      const status = (lead.la_connection_status || '').toLowerCase();
      return status.includes('accepted') || status.includes('connected');
    }).length;
    const acceptanceRate = totalConnections > 0 ? Math.round((connectionsAccepted / totalConnections) * 100) : 0;
    const messagesSent = allLinkedInLeads.reduce((sum, lead) => sum + (parseInt(lead.messages_sent) || 0), 0);
    const repliesReceived = allLinkedInLeads.reduce((sum, lead) => sum + (parseInt(lead.replies_received) || 0), 0);
    const replyRate = messagesSent > 0 ? Math.round((repliesReceived / messagesSent) * 100) : 0;
    const engagedLeads = allLinkedInLeads.filter(lead => {
      const temperature = (lead.lead_temprature || '').toLowerCase();
      return temperature.includes('hot') || temperature.includes('warm');
    }).length;
    const bookedDemos = allLinkedInLeads.filter(lead => {
      const status = (lead.lead_status || '').toLowerCase();
      return status.includes('booked');
    }).length;

    return { totalConnections, connectionsAccepted, acceptanceRate, messagesSent, repliesReceived, replyRate, engagedLeads, bookedDemos };
  }, [allLinkedInLeads]);

  const getTemperatureTag = (temperature: string) => {
    if (!temperature || temperature.trim() === '' || temperature.toLowerCase() === 'n/a') return null;
    const tempLower = temperature?.toLowerCase();
    if (tempLower?.includes('cold')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
        <Snowflake size={12} />
        Cold
      </span>
    );
    if (tempLower?.includes('warm')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
        <Sun size={12} />
        Warm
      </span>
    );
    if (tempLower?.includes('hot')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
        <Flame size={12} className="text-red-500" />
        Hot
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200">
        {temperature}
      </span>
    );
  };

  const getLeadStatusTag = getCampaignLeadStatusTag;

  const getConnectionStatusTag = (status: string) => {
    if (!status || status.trim() === '' || status.toLowerCase() === 'n/a') return null;
    const statusLower = status?.toLowerCase();
    if (statusLower?.includes('sent') || statusLower?.includes('pending')) return (
      <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[#1875FF]/6 border border-[#1875FF]/12">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1875FF] opacity-60"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1875FF]"></span>
        </span>
        <span className="text-xs font-semibold text-[#1875FF]">Sent</span>
      </div>
    );
    if (statusLower?.includes('accepted') || statusLower?.includes('connected')) return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#05E0D1]/8 border border-[#05E0D1]/15">
        <CheckCircle2 size={13} className="text-[#0bb5a5]" />
        <span className="text-xs font-semibold text-[#0bb5a5]">Accepted</span>
      </div>
    );
    if (statusLower?.includes('rejected') || statusLower?.includes('declined')) return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-rose-50 border border-rose-200">
        <XOctagon size={13} className="text-rose-500" />
        <span className="text-xs font-semibold text-rose-600">Rejected</span>
      </div>
    );
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
        <span className="text-xs font-semibold text-gray-600">{status}</span>
      </div>
    );
  };

  const getNextFollowUpTag = (datetime: string, leadStatus?: string) => {
    if (!datetime || datetime.trim() === '' || ['closed', 'booked'].includes((leadStatus || '').toLowerCase())) return <span className="text-xs text-gray-400">--</span>;
    try {
      const followUpDate = new Date(datetime);
      const now = new Date();
      const diffHours = (followUpDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      let IconComp = CalendarClock;
      let colorClasses = 'bg-[#05E0D1]/8 text-[#0bb5a5] border-[#05E0D1]/15';
      let dotColor = 'bg-[#05E0D1]';
      if (diffHours < 0) {
        IconComp = AlertCircle;
        colorClasses = 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/20';
        dotColor = 'bg-[#1875FF]';
      } else if (diffHours < 24) {
        IconComp = Clock;
        colorClasses = 'bg-gradient-to-r from-[#1875FF]/8 to-[#05E0D1]/8 text-[#1875FF] border-[#1875FF]/15';
        dotColor = 'bg-[#1875FF]/70';
      }
      return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border shadow-sm ${colorClasses}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
          <IconComp size={11} />
          <span className="text-xs font-semibold">{formatInTz(followUpDate, 'MMM dd, HH:mm', tz)}</span>
        </div>
      );
    } catch {
      return <span className="text-xs text-gray-400">Invalid</span>;
    }
  };

  const [selectedThreadAccountId, setSelectedThreadAccountId] = useState('');

  const handleOpenThread = (leadId: string, leadName: string, companyName: string, accountId?: string) => {
    setSelectedThreadLeadId(leadId);
    setSelectedThreadLeadName(leadName);
    setSelectedThreadCompanyName(companyName);
    setSelectedThreadAccountId(accountId || '');
    setShowThreadModal(true);
  };

  const handleCloseThread = () => {
    setShowThreadModal(false);
    setSelectedThreadLeadId('');
    setSelectedThreadLeadName('');
    setSelectedThreadCompanyName('');
    setSelectedThreadAccountId('');
  };

  const handleOpenSummary = (summary: string, leadName: string, companyName: string) => {
    setSelectedSummary(summary);
    setSelectedSummaryLeadName(leadName);
    setSelectedSummaryCompanyName(companyName);
    setShowSummaryModal(true);
  };

  const handleCloseSummary = () => {
    setShowSummaryModal(false);
    setSelectedSummary('');
    setSelectedSummaryLeadName('');
    setSelectedSummaryCompanyName('');
  };

  if (isLoading && allLinkedInLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate('/campaigns')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={20} className="mr-1" />
          Back to Campaigns
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 mb-6 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <StatsCard title="Connection Requests" value={stats.totalConnections} icon={UserPlus} color="blue" compact />
        <StatsCard title="Connections Accepted" value={stats.connectionsAccepted} icon={CheckCircle2} color="green" compact />
        <StatsCard title="Acceptance Rate" value={`${stats.acceptanceRate}%`} icon={TrendingUp} color="orange" compact />
        <StatsCard title="Messages Sent" value={stats.messagesSent} icon={Send} color="blue" compact />
        <StatsCard title="Replies Received" value={stats.repliesReceived} icon={Inbox} color="green" compact />
        <StatsCard title="Reply Rate" value={`${stats.replyRate}%`} icon={MessageCircle} color="orange" compact />
        <StatsCard title="Engaged Leads" value={stats.engagedLeads} icon={Users} color="blue" compact />
        <StatsCard title="Booked Demos" value={stats.bookedDemos} icon={Calendar} color="green" compact />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input type="text" placeholder="Search LinkedIn leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium" />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <SimpleCustomSelect value={statusFilter} onChange={setStatusFilter} options={[{ value: 'all', label: 'All Status' }, { value: 'assigned', label: 'Assigned' }, { value: 'queued', label: 'Queued' }, { value: 'messaging', label: 'Messaging' }, { value: 'chatting', label: 'Chatting' }, { value: 'booked', label: 'Booked' }, { value: 'follow up', label: 'Follow Up' }, { value: 'on hold', label: 'On Hold' }, { value: 'closed', label: 'Closed' }]} placeholder="All Status" className="w-48" />
              <SimpleCustomSelect value={temperatureFilter} onChange={setTemperatureFilter} options={[{ value: 'all', label: 'All Temperature' }, { value: 'cold', label: '❄️ Cold' }, { value: 'warm', label: '🌤️ Warm' }, { value: 'hot', label: '🔥 Hot' }]} placeholder="All Temperature" className="w-48" />
            </div>
          </div>
        </div>
      </div>

      {selectedLeadIds.length > 0 && (
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border-2 border-[#1875FF]/40 rounded-xl p-5 shadow-2xl shadow-[#1875FF]/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-lg flex items-center justify-center animate-pulse shadow-md">
                <CheckCircle className="text-[#1875FF]" size={20} />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">
                  {selectAllLeads ? `All ${filteredLeads.length}` : selectedLeadIds.length} lead{(selectAllLeads ? filteredLeads.length : selectedLeadIds.length) > 1 ? 's' : ''} selected
                </span>
                {showSelectAllBanner && (
                  <button
                    onClick={handleSelectAllLeads}
                    className="text-[#1875FF] font-semibold hover:underline focus:outline-none text-sm text-left"
                  >
                    Select all {filteredLeads.length} leads
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkStatusChange('active')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'active' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Play size={18} />
                    <span>Activate</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleBulkStatusChange('on hold')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#05E0D1] to-[#1875FF] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#05E0D1]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'on hold' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Pause size={18} />
                    <span>On Hold</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleBulkStatusChange('closed')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'closed' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    <span>Close</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-center px-2 py-4 w-12">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={linkedInLeads.length > 0 && selectedLeadIds.length === linkedInLeads.map(lead => lead.lead_id).filter(Boolean).length}
                      ref={(el) => {
                        if (el) {
                          const allFilteredIds = linkedInLeads.map(lead => lead.lead_id).filter(Boolean);
                          el.indeterminate = selectedLeadIds.length > 0 && selectedLeadIds.length < allFilteredIds.length;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                    />
                  </div>
                </th>
                <th className="text-left px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-36">Person</th>
                <th className="text-left px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-32">Company</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-24">Connection Status</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-20">
                  <div>Messages</div>
                  <div className="font-normal text-[10px] text-gray-500">Sent / Received</div>
                </th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-24">Next Follow Up</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-20">Temperature</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-20">Lead Status</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-16">Convo Record</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {linkedInLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-[#1875FF]/5 transition-colors">
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={lead.lead_id ? selectedLeadIds.includes(lead.lead_id) : false}
                        onChange={() => handleSelectLead(lead.lead_id)}
                        className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-4">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium text-gray-900 break-words">{lead.dm_full_name || 'N/A'}</span>
                      {lead.dm_linkedin && <a href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="LinkedIn Profile"><Linkedin size={12} /></a>}
                    </div>
                    <div className="text-xs text-gray-500 break-words">{lead.dm_title || 'N/A'}</div>
                  </td>
                  <td className="px-2 py-4">
                    <div className="text-sm font-medium text-gray-900 break-words mb-2">{lead.company_name || 'N/A'}</div>
                    <div className="flex items-center space-x-2">
                      {lead.company_website && <a href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="Visit Website"><Globe size={14} /></a>}
                      {lead.company_linkedin && <a href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="LinkedIn Profile"><Linkedin size={14} /></a>}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center">{getConnectionStatusTag(lead.la_connection_status)}</td>
                  <td className="px-2 py-4 text-center">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 border border-[#1875FF]/10">
                      <div className="flex items-center gap-1">
                        <Send size={10} className="text-[#1875FF]" />
                        <span className="text-xs font-bold text-[#1875FF]">{lead.messages_sent || 0}</span>
                      </div>
                      <div className="w-px h-3 bg-gray-300"></div>
                      <div className="flex items-center gap-1">
                        <Inbox size={10} className="text-[#05E0D1]" />
                        <span className="text-xs font-bold text-[#0bb5a5]">{lead.replies_received || 0}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center">{getNextFollowUpTag(lead.next_fu_datetime, lead.lead_status)}</td>
                  <td className="px-2 py-4 text-center">{getTemperatureTag(lead.lead_temprature)}</td>
                  <td className="px-2 py-4 text-center">{getLeadStatusTag(lead.lead_status)}</td>
                  <td className="px-2 py-4 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      {lead.lead_id && (
                        <button
                          onClick={() => handleOpenThread(lead.lead_id, lead.dm_full_name || 'Lead', lead.company_name || '', lead.la_linkedin_account_id)}
                          className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                          title="View LinkedIn Thread"
                        >
                          <MessageSquare size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                        </button>
                      )}
                      {lead.convo_summary && (
                        <button
                          onClick={() => handleOpenSummary(lead.convo_summary, lead.dm_full_name || 'Lead', lead.company_name || '')}
                          className="group relative w-7 h-7 rounded-lg border border-[#05E0D1]/20 bg-[#05E0D1]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#05E0D1] hover:to-[#1875FF] hover:border-transparent hover:shadow-md hover:shadow-[#05E0D1]/25 transition-all duration-200"
                          title="View Summary"
                        >
                          <FileText size={13} className="text-[#0bb5a5] group-hover:text-white transition-colors duration-200" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLeads.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-bold">No LinkedIn leads found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">{searchTerm || statusFilter !== 'all' || temperatureFilter !== 'all' ? 'Try adjusting your filters' : 'LinkedIn leads will appear here once they\'re added to the system'}</div>
        </div>
      )}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredLeads.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      <LinkedInThreadModal
        isOpen={showThreadModal}
        onClose={handleCloseThread}
        leadId={selectedThreadLeadId}
        leadName={selectedThreadLeadName}
        companyName={selectedThreadCompanyName}
        agentName={getAgentLabel(selectedThreadAccountId || undefined)}
      />

      <ConvoSummaryModal
        isOpen={showSummaryModal}
        onClose={handleCloseSummary}
        summary={selectedSummary}
        leadName={selectedSummaryLeadName}
        companyName={selectedSummaryCompanyName}
      />
    </div>
  );
};

export default LinkedInCampaignPage;
